import React from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { Check, Circle, ExternalLink, Settings, Share2, Play, Video, Crown } from 'lucide-react';
import { getEmbedUrl, isDirectVideoFile, buildAssetUrl } from './course/LessonPlayer';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// First-login welcome modal — 4-step Founder onboarding checklist with an
// admin-configurable welcome video. Progress persists per account.
export default function OnboardingModal({ open, onOpenChange, status, setStatus, getAuthHeader }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  if (!status) return null;
  // A forced password rotation takes absolute precedence — don't render the
  // onboarding dialog (its focus-trap would block the password modal).
  if (user?.must_change_password) return null;

  const markStep = async (stepId) => {
    try {
      await axios.post(`${API_URL}/api/onboarding/steps/${stepId}/complete`, {}, { headers: getAuthHeader() });
      setStatus((s) => {
        if (!s) return s;
        const steps = { ...s.steps, [stepId]: true };
        const completed_count = Object.values(steps).filter(Boolean).length;
        return { ...s, steps, completed_count, completed: completed_count === s.total_steps };
      });
    } catch {
      /* non-fatal — signal-based completion still applies server-side */
    }
  };

  const shareCard = async () => {
    const url = status.card_url;
    if (!url) {
      toast.error('Card link not ready yet — try again in a moment');
      return;
    }
    const shareData = { title: 'My WeMasterminds Digital Card', text: 'Add me on WeMasterminds', url };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Card link copied to clipboard');
      }
      axios.post(`${API_URL}/api/wemm/setup/mark-shared`, {}, { headers: getAuthHeader() }).catch(() => {});
      markStep('share_card');
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error('Could not share the card');
    }
  };

  const skip = async () => {
    onOpenChange(false);
    try {
      await axios.post(`${API_URL}/api/onboarding/dismiss`, {}, { headers: getAuthHeader() });
      setStatus((s) => (s ? { ...s, dismissed: true, should_show: false } : s));
    } catch {
      /* already closed locally */
    }
  };

  const steps = [
    status.is_founder && {
      id: 'claim_wemm',
      title: 'Claim your Business Card account',
      description:
        'Go to WeMillionairesMM.com and claim the business card account that was provisioned for you. This is for Founders only.',
      actionLabel: 'Claim my account',
      icon: ExternalLink,
      onAction: () => {
        window.open('https://wemillionairesmm.com', '_blank', 'noopener');
        markStep('claim_wemm');
      },
      badge:
        status.steps.claim_wemm || status.wemm_active ? "You're active on WeMillionairesMM" : null,
    },
    {
      id: 'personalize_profile',
      title: 'Personalize your profile',
      description:
        'Go to your profile and personalize it. Make sure to paste your WeMillionairesMM URL in: Profile Settings → Micro-site → WeMillionairesMM link.',
      actionLabel: 'Open Settings',
      icon: Settings,
      onAction: () => {
        markStep('personalize_profile');
        onOpenChange(false);
        navigate('/profile/settings?tab=microsite');
      },
    },
    {
      id: 'share_card',
      title: 'Share your Digital Card',
      description: 'Share your digital card and get it in front of your first lead.',
      actionLabel: 'Share my card',
      icon: Share2,
      onAction: shareCard,
    },
    {
      id: 'start_training',
      title: 'Start your training',
      description:
        'Go to the Learning tab and select the WeMasterminds training to start your first lesson — this puts you on the fast track to success.',
      actionLabel: 'Start lesson',
      icon: Play,
      onAction: () => {
        markStep('start_training');
        onOpenChange(false);
        navigate(status.training_course_id ? `/courses/${status.training_course_id}` : '/courses');
      },
    },
  ].filter(Boolean);

  const progressPct = status.total_steps ? (status.completed_count / status.total_steps) * 100 : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto bg-brand-surface border-white/10 p-0"
        data-testid="onboarding-modal"
      >
        <div className="p-6 space-y-5">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-brand-gold/20 border border-brand-gold/30 flex items-center justify-center flex-shrink-0">
              <Crown className="w-5 h-5 text-brand-gold" />
            </div>
            <div>
              <DialogTitle className="text-2xl font-playfair text-white" data-testid="onboarding-title">
                {status.is_founder ? 'Welcome, Founder! 🎉' : 'Welcome! 🎉'}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-400">
                Complete your {status.is_founder ? 'Founder ' : ''}onboarding setup
              </DialogDescription>
            </div>
          </div>

          {/* Welcome video slot */}
          <div className="rounded-xl overflow-hidden border border-white/10 bg-black aspect-video">
            {status.video_url ? (
              isDirectVideoFile(status.video_url) ? (
                <video
                  src={buildAssetUrl(status.video_url)}
                  className="w-full h-full"
                  controls
                  playsInline
                  preload="metadata"
                  data-testid="onboarding-video"
                />
              ) : (
                <iframe
                  src={getEmbedUrl(status.video_url)}
                  className="w-full h-full"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  title="Welcome video"
                  data-testid="onboarding-video-iframe"
                />
              )
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-gray-500" data-testid="onboarding-video-placeholder">
                <Video className="w-10 h-10 text-brand-gold/40" />
                <p className="text-xs">Welcome video coming soon</p>
              </div>
            )}
          </div>

          {/* Progress */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs uppercase tracking-widest text-brand-gold font-semibold">Your setup</p>
              <p className="text-sm text-white" data-testid="onboarding-progress">
                <span className="text-brand-gold font-semibold">{status.completed_count}</span> of {status.total_steps} complete
              </p>
            </div>
            <Progress value={progressPct} className="h-1.5" />
          </div>

          {/* Steps */}
          <ol className="space-y-2.5">
            {steps.map((step, i) => {
              const done = !!status.steps[step.id];
              return (
                <li
                  key={step.id}
                  className={`flex items-start gap-3 p-3.5 rounded-xl border transition ${
                    done ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-white/[0.02] border-white/5'
                  }`}
                  data-testid={`onboarding-step-${step.id}`}
                >
                  <div className="mt-0.5 flex-shrink-0">
                    {done ? (
                      <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center" data-testid={`step-done-${step.id}`}>
                        <Check className="w-3 h-3 text-black" />
                      </div>
                    ) : (
                      <Circle className="w-5 h-5 text-white/30" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${done ? 'text-emerald-200' : 'text-white'}`}>
                      Step {i + 1} — {step.title}
                    </p>
                    <p className="text-xs text-white/50 mt-0.5 leading-relaxed">{step.description}</p>
                    {step.badge && (
                      <span
                        className="inline-flex items-center gap-1 mt-2 text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/25"
                        data-testid="wemm-active-badge"
                      >
                        <Check className="w-3 h-3" /> {step.badge}
                      </span>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={step.onAction}
                    className={`flex-shrink-0 text-xs ${
                      done
                        ? 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
                        : 'bg-brand-gold text-black hover:brightness-110'
                    }`}
                    data-testid={`onboarding-action-${step.id}`}
                  >
                    <step.icon className="w-3.5 h-3.5 mr-1.5" />
                    {step.actionLabel}
                  </Button>
                </li>
              );
            })}
          </ol>

          {/* Footer */}
          <div className="flex items-center justify-between pt-1">
            <button
              onClick={skip}
              className="text-xs text-gray-400 hover:text-white underline underline-offset-4 transition-colors"
              data-testid="onboarding-skip"
            >
              Skip for now
            </button>
            {status.completed && (
              <p className="text-sm text-emerald-400 font-medium" data-testid="onboarding-complete-msg">
                All set — welcome aboard! 🎉
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
